import React, { useState, useEffect } from "react";
import { 
    Card, Row, Col, Button, Badge, Table, Spinner, Alert,
    ListGroup, ProgressBar, Modal
} from "react-bootstrap";
import { useParams, useNavigate, Link } from "react-router-dom";
import { formatCurrency, formatDate } from "../utils/formatters";
import { API_BASE_URL } from "../constants/api";
import Footer from "../layouts/Footer";
import Header from "../layouts/Header";


export default function DetailFournisseur() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [fournisseur, setFournisseur] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    useEffect(() => {
        fetchFournisseur();
    }, [id]);

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${fournisseur.id_fournisseur}`,
                {
                    method: 'DELETE',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            
            const result = await response.json();
            
            if (result.success) {
                setShowDeleteModal(false);
                navigate('/achats/fournisseurs');
            } else {
                alert(result.message || "Erreur lors de la suppression");
                setShowDeleteModal(false);
            }
        } catch (err) {
            alert("Erreur de connexion au serveur");
            setShowDeleteModal(false);
        } finally {
            setIsDeleting(false);
        }
    };

    const fetchFournisseur = async () => {
        try {
            setLoading(true);
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${API_BASE_URL}/fournisseurs.php?id=${id}`,
                { headers: { 'Authorization': `Bearer ${token}` } }
            );
            
            if (!response.ok) throw new Error("Erreur chargement");
            
            const result = await response.json();
            setFournisseur(result.data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const getNoteStars = (note) => {
        const noteValue = note ? Math.round(parseFloat(note)) : 0;
        const stars = [];
        for (let i = 1; i <= 5; i++) {
            stars.push(
                <i key={i} className={`ri-star-${i <= noteValue ? 'fill' : 'line'} text-warning`} style={{ fontSize: '1.2rem' }}></i>
            );
        }
        return stars;
    };

    const getStatutBadge = (statut) => {
        const configs = {
            recue_complete: { color: '#10b981', label: 'Reçue complète', icon: 'ri-check-double-line' },
            recue_partielle: { color: '#f59e0b', label: 'Reçue partielle', icon: 'ri-stack-line' },
            brouillon: { color: '#64748b', label: 'Brouillon', icon: 'ri-draft-line' },
            envoyee: { color: '#3b82f6', label: 'Envoyée', icon: 'ri-send-plane-line' },
            annulee: { color: '#ef4444', label: 'Annulée', icon: 'ri-close-circle-line' }
        };
        const config = configs[statut] || { color: '#64748b', label: statut, icon: 'ri-question-line' };
        
        return (
            <span className="status-badge" style={{ background: `${config.color}15`, color: config.color }}>
                <i className={`${config.icon} me-1 fs-10`}></i>
                {config.label}
            </span>
        );
    };

    if (loading) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                    <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 'calc(100vh - 100px)' }}>
                        <div className="text-center">
                            <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem', color: '#21ACCD' }} role="status">
                                <span className="visually-hidden">Chargement...</span>
                            </div>
                            <h5 className="text-muted">Chargement des informations du fournisseur...</h5>
                        </div>
                    </div>
                </div>
                <Footer />
            </>
        );
    }

    if (error || !fournisseur) {
        return (
            <>
                <Header />
                <div className="main main-app p-3 p-lg-4">
                    <Alert variant="danger" className="border-0 shadow-sm rounded-3">
                        <div className="d-flex align-items-center">
                            <i className="ri-error-warning-line fs-1 me-3 text-danger"></i>
                            <div>
                                <h6 className="mb-1 fw-bold">Fournisseur non trouvé</h6>
                                <p className="mb-0 small">{error || "Le fournisseur demandé n'existe pas"}</p>
                                <Button variant="outline-danger" size="sm" className="mt-2 rounded-pill" onClick={() => navigate('/achats/fournisseurs')}>
                                    <i className="ri-arrow-left-line me-1"></i>Retour à la liste
                                </Button>
                            </div>
                        </div>
                    </Alert>
                </div>
                <Footer />
            </>
        );
    }

    return (
        <>
            <Header />
            <div className="main main-app p-3 p-lg-4" style={{ background: '#f8fafc', minHeight: '100vh' }}>
                <div className="container-fluid">
                    {/* Fil d'Ariane */}
                    <div className="d-flex align-items-center gap-2 mb-4">
                        <Button 
                            variant="link" 
                            className="p-0 text-muted text-decoration-none" 
                            onClick={() => navigate('/achats/fournisseurs')}
                        >
                            <i className="ri-arrow-left-line me-1"></i>Fournisseurs
                        </Button>
                        <i className="ri-arrow-right-s-line text-muted"></i>
                        <span className="text-primary">Fiche fournisseur</span>
                    </div>

                    {/* En-tête avec carte profil */}
                    <Card className="border-0 shadow-sm rounded-3 mb-4 overflow-hidden">
                        <div className="profile-header" style={{ 
                            height: '120px', 
                            background: 'linear-gradient(135deg, #21ACCD, #1a8ba8)',
                            position: 'relative'
                        }}>
                            <div className="profile-bg-shape"></div>
                        </div>
                        <Card.Body className="position-relative" style={{ marginTop: '-60px' }}>
                            <Row className="align-items-end">
                                <Col md={8}>
                                    <div className="d-flex align-items-end gap-4 flex-wrap">
                                        <div className="profile-avatar">
                                            <div className="avatar-circle">
                                                <i className="ri-truck-line"></i>
                                            </div>
                                        </div>
                                        <div className="mb-2">
                                            <h1 className="display-6 fw-bold mb-1" style={{ color: '#fff' }}>
                                                {fournisseur.raison_sociale}
                                            </h1>
                                            <div className="d-flex align-items-center gap-3 flex-wrap">
                                                <Badge className="px-3 py-2 rounded-pill" style={{ background: '#21ACCD' }}>
                                                    <i className="ri-barcode-line me-1"></i>
                                                    Code: {fournisseur.code_fournisseur}
                                                </Badge>
                                                <span className="text-muted">
                                                    <i className="ri-calendar-line me-1"></i>
                                                    Fournisseur depuis {formatDate(fournisseur.date_creation)}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col md={4}>
                                    <div className="d-flex justify-content-md-end gap-2 mt-3 mt-md-0">
                                        <Button  
                                            variant="outline-primary" 
                                            onClick={() => navigate('/achats/commandesNouveau')}
                                            className="rounded-pill px-4"
                                            style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                        >
                                            <i className="ri-add-line me-2"></i>
                                            Nouvelle commande
                                        </Button>
                                        <Button 
                                            variant="primary"
                                            as={Link}
                                            to={`/achats/fournisseurs/modifier/${fournisseur.id_fournisseur}`}
                                            className="rounded-pill px-4"
                                            style={{ background: '#21ACCD', border: 'none' }}
                                        >
                                            <i className="ri-pencil-line me-2"></i>
                                            Modifier
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* KPI Cards */}
                    <Row className="g-3 mb-4">
                        <Col md={4}>
                            <div className="kpi-card kpi-primary">
                                <div className="kpi-icon">
                                    <i className="ri-shopping-bag-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Total achats</span>
                                    <h3 className="kpi-value">{formatCurrency(fournisseur.total_achats || 0)}</h3>
                                    <span className="kpi-unit">{fournisseur.nb_commandes || 0} commandes</span>
                                </div>
                            </div>
                        </Col>

                        <Col md={4}>
                            <div className="kpi-card kpi-success">
                                <div className="kpi-icon">
                                    <i className="ri-calendar-check-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Dernière commande</span>
                                    <h3 className="kpi-value" style={{ fontSize: '1.1rem' }}>
                                        {fournisseur.derniere_commande ? formatDate(fournisseur.derniere_commande) : 'Jamais'}
                                    </h3>
                                    <span className="kpi-unit">
                                        {fournisseur.derniere_commande ? 
                                            `${Math.round((new Date() - new Date(fournisseur.derniere_commande)) / (1000 * 60 * 60 * 24))} jours` : 
                                            '-'}
                                    </span>
                                </div>
                            </div>
                        </Col>

                        <Col md={4}>
                            <div className="kpi-card kpi-warning">
                                <div className="kpi-icon">
                                    <i className="ri-star-line fs-24"></i>
                                </div>
                                <div className="kpi-content">
                                    <span className="kpi-label">Note moyenne</span>
                                    <h3 className="kpi-value">
                                        {fournisseur.note_moyenne ? parseFloat(fournisseur.note_moyenne).toFixed(1) : 'N/A'}
                                    </h3>
                                    <div className="mt-1">
                                        {getNoteStars(fournisseur.note_moyenne)}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Informations détaillées */}
                    <Row className="g-4 mb-4">
                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-contacts-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Coordonnées
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <div className="info-row">
                                        <span className="info-label">
                                            <i className="ri-phone-line me-2"></i>
                                            Téléphone
                                        </span>
                                        <span className="info-value">{fournisseur.telephone || 'Non renseigné'}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">
                                            <i className="ri-mail-line me-2"></i>
                                            Email
                                        </span>
                                        <span className="info-value">{fournisseur.email || 'Non renseigné'}</span>
                                    </div>
                                    <div className="info-row">
                                        <span className="info-label">
                                            <i className="ri-map-pin-line me-2"></i>
                                            Adresse
                                        </span>
                                        <span className="info-value">{fournisseur.adresse || 'Non renseignée'}</span>
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col md={6}>
                            <Card className="border-0 shadow-sm rounded-3 h-100">
                                <Card.Header className="bg-transparent border-0 pt-4">
                                    <h6 className="mb-0 fw-semibold">
                                        <i className="ri-briefcase-line me-2" style={{ color: '#21ACCD' }}></i>
                                        Identifiants juridiques
                                    </h6>
                                </Card.Header>
                                <Card.Body>
                                    <Row>
                                        <Col md={6}>
                                            <div className="info-row">
                                                <span className="info-label">ICE</span>
                                                <span className="info-value">{fournisseur.ice || '-'}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="info-label">Patente</span>
                                                <span className="info-value">{fournisseur.patente || '-'}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="info-label">RC</span>
                                                <span className="info-value">{fournisseur.rc || '-'}</span>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="info-row">
                                                <span className="info-label">IF</span>
                                                <span className="info-value">{fournisseur.identifiant_fiscal || '-'}</span>
                                            </div>
                                            <div className="info-row">
                                                <span className="info-label">CNSS</span>
                                                <span className="info-value">{fournisseur.cnss || '-'}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    {fournisseur.conditions_reglement && (
                                        <div className="comment-box mt-3">
                                            <div className="comment-header">
                                                <i className="ri-file-text-line me-2"></i>
                                                Conditions de règlement
                                            </div>
                                            <div className="comment-content">
                                                {fournisseur.conditions_reglement}
                                            </div>
                                        </div>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>

                    {/* Dernières commandes */}
                    {fournisseur.commandes?.length > 0 && (
                        <Card className="border-0 shadow-sm rounded-3">
                            <Card.Header className="bg-transparent border-0 pt-4 d-flex justify-content-between align-items-center">
                                <h6 className="mb-0 fw-semibold">
                                    <i className="ri-file-list-line me-2" style={{ color: '#21ACCD' }}></i>
                                    Dernières commandes
                                </h6>
                                <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    className="rounded-pill px-3"
                                    onClick={() => navigate('/achats/commandes')}
                                    style={{ borderColor: '#21ACCD', color: '#21ACCD' }}
                                >
                                    <i className="ri-eye-line me-1"></i>
                                    Voir tout
                                </Button>
                            </Card.Header>
                            <Card.Body className="p-0">
                                <div className="table-responsive">
                                    <Table hover className="align-middle mb-0 custom-table">
                                        <thead className="bg-light">
                                            <tr>
                                                <th className="ps-4">N° Commande</th>
                                                <th>Date</th>
                                                <th className="text-end">Montant</th>
                                                <th>Statut</th>
                                                <th className="text-center pe-4">Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {fournisseur.commandes.map((cmd) => (
                                                <tr key={cmd.id_commande_fournisseur} className="commande-row">
                                                    <td className="ps-4">
                                                        <Link to={`/achats/commandes/${cmd.id_commande_fournisseur}`} className="table-link">
                                                            {cmd.numero_commande}
                                                        </Link>
                                                    </td>
                                                    <td>{formatDate(cmd.date_commande)}</td>
                                                    <td className="text-end fw-bold text-primary">
                                                        {formatCurrency(cmd.montant_ttc)}
                                                    </td>
                                                    <td>{getStatutBadge(cmd.statut)}</td>
                                                    <td className="text-center pe-4">
                                                        <Button 
                                                            variant="outline-info" 
                                                            size="sm"
                                                            className="action-btn"
                                                            onClick={() => navigate(`/achats/commandes/${cmd.id_commande_fournisseur}`)}
                                                        >
                                                            <i className="ri-eye-line"></i>
                                                        </Button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Métadonnées */}
                    <Card className="border-0 shadow-sm rounded-3 mt-4" style={{ background: '#f8fafc' }}>
                        <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                <div className="text-muted small">
                                    <i className="ri-information-line me-1"></i>
                                    ID: #{fournisseur.id_fournisseur} • 
                                    Créé le {formatDate(fournisseur.date_creation)}
                                    {fournisseur.date_modification && ` • Modifié le ${formatDate(fournisseur.date_modification)}`}
                                </div>
                                <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    className="rounded-pill"
                                    onClick={() => setShowDeleteModal(true)}
                                >
                                    <i className="ri-delete-bin-line me-1"></i>
                                    Supprimer
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </div>

                {/* Modal de confirmation de suppression */}
                <Modal 
                    show={showDeleteModal} 
                    onHide={() => setShowDeleteModal(false)} 
                    centered 
                    className="modern-modal delete-modal"
                >
                    <Modal.Header closeButton className="border-0 pb-0" style={{ background: '#dc2626' }}>
                        <Modal.Title className="w-100">
                            <div className="text-center">
                                <div className="confirm-icon mb-3">
                                    <i className="ri-delete-bin-line text-white" style={{ fontSize: '4rem' }}></i>
                                </div>
                                <h3 className="fw-bold text-white">Supprimer le fournisseur</h3>
                            </div>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-4 text-center">
                        <p className="text-muted mb-4">
                            Êtes-vous sûr de vouloir supprimer <strong className="text-primary">{fournisseur?.raison_sociale}</strong> ?
                        </p>
                        <Alert variant="warning" className="rounded-3 text-start">
                            <div className="d-flex">
                                <i className="ri-alert-line fs-4 me-3 flex-shrink-0"></i>
                                <div>
                                    <strong>Cette action est irréversible</strong>
                                    <p className="mb-0 small mt-1">
                                        La suppression entraînera la perte définitive de toutes les informations 
                                        liées à ce fournisseur. Les commandes existantes ne seront pas affectées.
                                    </p>
                                </div>
                            </div>
                        </Alert>
                        <div className="bg-light p-3 rounded-3 mb-3">
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Code fournisseur :</span>
                                <span className="fw-semibold">{fournisseur?.code_fournisseur}</span>
                            </div>
                            <div className="d-flex justify-content-between mb-2">
                                <span className="text-muted">Total commandes :</span>
                                <span className="fw-semibold">{fournisseur?.nb_commandes || 0}</span>
                            </div>
                            <div className="d-flex justify-content-between">
                                <span className="text-muted">Total achats :</span>
                                <span className="fw-semibold text-primary">{formatCurrency(fournisseur?.total_achats || 0)}</span>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0 pb-4 justify-content-center">
                        <Button 
                            variant="outline-secondary" 
                            onClick={() => setShowDeleteModal(false)}
                            className="px-4 rounded-pill"
                            disabled={isDeleting}
                        >
                            Annuler
                        </Button>
                        <Button 
                            variant="danger" 
                            onClick={handleDelete}
                            className="px-5 rounded-pill"
                            disabled={isDeleting}
                        >
                            {isDeleting ? (
                                <>
                                    <Spinner size="sm" className="me-2" />
                                    Suppression...
                                </>
                            ) : (
                                <>
                                    <i className="ri-delete-bin-line me-2"></i>
                                    Supprimer
                                </>
                            )}
                        </Button>
                    </Modal.Footer>
                </Modal>

                <Footer />
            </div>

            <style>{`
                /* Profile Header */
                .profile-header {
                    position: relative;
                    overflow: hidden;
                }
                
                .profile-bg-shape {
                    position: absolute;
                    top: -20px;
                    right: 20px;
                    width: 200px;
                    height: 200px;
                    background: rgba(255, 255, 255, 0.1);
                    border-radius: 50%;
                    pointer-events: none;
                }
                
                .profile-avatar {
                    margin-bottom: -10px;
                }
                
                .avatar-circle {
                    width: 100px;
                    height: 100px;
                    background: white;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 20px 25px -5px rgba(0,0,0,0.1);
                }
                
                .avatar-circle i {
                    font-size: 48px;
                    color: #21ACCD;
                }
                
                /* KPI Cards */
                .kpi-card {
                    background: white;
                    border-radius: 0px;
                    padding: 1rem;
                    display: flex;
                    align-items: center;
                    gap: 1rem;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.05);
                    transition: all 0.2s ease;
                    height: 100%;
                    border-left: 2px solid;
                }
                .kpi-card:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 8px 20px rgba(0,0,0,0.08);
                }
                .kpi-primary { border-left-color: #21ACCD; }
                .kpi-success { border-left-color: #10b981; }
                .kpi-warning { border-left-color: #f59e0b; }
                
                .kpi-icon {
                    width: 48px;
                    height: 48px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                }
                .kpi-primary .kpi-icon { background: #e2f3f5; color: #21ACCD; }
                .kpi-success .kpi-icon { background: #ecfdf5; color: #10b981; }
                .kpi-warning .kpi-icon { background: #fffbeb; color: #f59e0b; }
                
                .kpi-content {
                    flex: 1;
                }
                .kpi-label {
                    font-size: 0.7rem;
                    text-transform: uppercase;
                    color: #6c757d;
                    font-weight: 600;
                    letter-spacing: 0.5px;
                }
                .kpi-value {
                    font-size: 1.3rem;
                    font-weight: 700;
                    margin: 0.25rem 0;
                    color: #1a3c5e;
                }
                .kpi-unit {
                    font-size: 0.7rem;
                    color: #6c757d;
                }
                
                /* Info Rows */
                .info-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 0.75rem 0;
                    border-bottom: 1px dashed #e2e8f0;
                }
                
                .info-row:last-child {
                    border-bottom: none;
                }
                
                .info-label {
                    display: flex;
                    align-items: center;
                    color: #64748b;
                    font-size: 0.85rem;
                }
                
                .info-value {
                    font-weight: 500;
                    color: #1e293b;
                }
                
                /* Comment Box */
                .comment-box {
                    margin-top: 1rem;
                    padding: 1rem;
                    background: #f8fafc;
                    border-radius: 12px;
                    border-left: 3px solid #21ACCD;
                }
                
                .comment-header {
                    font-weight: 600;
                    margin-bottom: 0.5rem;
                    color: #21ACCD;
                }
                
                .comment-content {
                    font-size: 0.85rem;
                    color: #475569;
                }
                
                /* Status Badge */
                .status-badge {
                    display: inline-flex;
                    align-items: center;
                    padding: 0.3rem 0.8rem;
                    border-radius: 30px;
                    font-size: 0.7rem;
                    font-weight: 500;
                }
                
                /* Table Link */
                .table-link {
                    color: #21ACCD;
                    text-decoration: none;
                    font-weight: 600;
                }
                .table-link:hover {
                    text-decoration: underline;
                }
                
                /* Custom Table */
                .custom-table thead th {
                    background: #f8fafc;
                    color: #1e293b;
                    font-weight: 600;
                    font-size: 0.75rem;
                    text-transform: uppercase;
                    letter-spacing: 0.5px;
                    padding: 1rem 0.75rem;
                    border-bottom: 2px solid #e2e8f0;
                }
                
                .custom-table tbody td {
                    padding: 0.75rem;
                    vertical-align: middle;
                    border-bottom: 1px solid #f1f5f9;
                }
                
                .commande-row {
                    transition: all 0.2s ease;
                }
                
                .commande-row:hover {
                    background: #f8fafc;
                }
                
                /* Action Button */
                .action-btn {
                    width: 32px;
                    height: 32px;
                    border-radius: 8px;
                    display: inline-flex;
                    align-items: center;
                    justify-content: center;
                    transition: all 0.2s ease;
                    border: 1px solid #e2e8f0;
                    background: white;
                    color: #64748b;
                }
                .action-btn:hover {
                    transform: translateY(-2px);
                    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
                }
                .btn-outline-info:hover {
                    background: linear-gradient(135deg, #0891b2, #0e7490);
                    border-color: #0891b2;
                    color: white;
                }
                
                /* Modern Modal */
                .modern-modal .modal-content {
                    border-radius: 20px;
                    border: none;
                    box-shadow: 0 20px 40px rgba(0,0,0,0.2);
                    overflow: hidden;
                }
                
                .confirm-icon {
                    animation: scaleIn 0.5s ease-out;
                }
                
                @keyframes scaleIn {
                    0% { transform: scale(0); opacity: 0; }
                    50% { transform: scale(1.2); }
                    100% { transform: scale(1); opacity: 1; }
                }
                
                .rounded-3 {
                    border-radius: 12px !important;
                }
                
                .rounded-pill {
                    border-radius: 50px !important;
                }
                
                .fs-10 {
                    font-size: 0.625rem;
                }
                .fs-20 {
                    font-size: 1.25rem;
                }
                .fs-24 {
                    font-size: 1.5rem;
                }
            `}</style>
        </>
    );
}